import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import { ChartType } from '@portal/shared/components/device-details-chart/device-details-chart.types';
import { OrderDirection } from '@portal/shared/types/listing/filters.types';
import { DateTimeInterval } from '@portal/shared/types/datetime.types';
import { TabType } from '@portal/shared/types/tab.types';
import { EventFieldName } from '@portal/shared/entities/events';
import { EventsApiService } from 'src/app/services/api/events/events-api.service';

import { DeviceDetailsMetricsService } from '../../../device-details-metrics.service';
import { DeviceDetailsMetricsActions } from '../../../store/device-details-metrics.actions';
import { selectDateRange, selectDeviceDetails } from '../../../store/device-details-metrics.reducer';
import { RmdMethaneLevelTabContentActions } from './rmd-methane-level-tab-content.actions';
import { rmdMethaneLevelTabContentFeature } from './rmd-methane-level-tab-content.reducer';

const EVENTS_INTERVAL = DateTimeInterval.Day;

export const requestEvents$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    deviceDetailsMetricsService = inject(DeviceDetailsMetricsService)
  ) =>
    actions$.pipe(
      ofType(
        RmdMethaneLevelTabContentActions.setChartRenderingModeTab,
        RmdMethaneLevelTabContentActions.setChartType,
        DeviceDetailsMetricsActions.setDeviceDetails,
        DeviceDetailsMetricsActions.setDateRange
      ),
      withLatestFrom(
        store.select(rmdMethaneLevelTabContentFeature.selectSelectedChartRenderingModeTab),
        store.select(rmdMethaneLevelTabContentFeature.selectSelectedChartType),
        store.select(rmdMethaneLevelTabContentFeature.selectEventsParams),
        store.select(selectDeviceDetails),
        store.select(selectDateRange)
      ),
      filter(
        ([, renderingMode, chartType, , deviceDetails, dateRange]) =>
          renderingMode === TabType.Events &&
          chartType !== ChartType.Table &&
          !!deviceDetails?.deviceUID &&
          !!dateRange?.start &&
          !!dateRange?.end
      ),
      map(([, , , loadedParams, deviceDetails, dateRange]) => ({
        loadedParams,
        params: {
          deviceUID: deviceDetails!.deviceUID,
          ...deviceDetailsMetricsService.getHealthDateRange(dateRange!.start, dateRange!.end)
        }
      })),
      filter(
        ({ loadedParams, params }) =>
          loadedParams?.deviceUID !== params.deviceUID ||
          loadedParams.startDateUtc !== params.startDateUtc ||
          loadedParams.endDateUtc !== params.endDateUtc
      ),
      map(({ params }) => RmdMethaneLevelTabContentActions.getEvents({ params }))
    ),
  { functional: true }
);

export const getEvents$ = createEffect(
  (actions$ = inject(Actions), eventsApiService = inject(EventsApiService)) =>
    actions$.pipe(
      ofType(RmdMethaneLevelTabContentActions.getEvents),
      switchMap(({ params: { deviceUID, startDateUtc, endDateUtc } }) =>
        eventsApiService
          .getIntervalAggregatedEvents({
            filters: [
              { filterType: EventFieldName.LastDetectedUtc, filteredValues: [startDateUtc, endDateUtc] },
              { filterType: EventFieldName.DeviceUID, filteredValues: [deviceUID] }
            ],
            interval: EVENTS_INTERVAL,
            sort: { columnName: EventFieldName.LastDetectedUtc, orderDirection: OrderDirection.DESC }
          })
          .pipe(
            map(events => RmdMethaneLevelTabContentActions.getEventsSuccess({ events })),
            catchError(() => of(RmdMethaneLevelTabContentActions.getEventsFailure({ error: 'Failed to load events' })))
          )
      )
    ),
  { functional: true }
);
